import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Mail, Plus, Trash2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Admin-managed list of email addresses that receive system alerts.
 * Backed by the `system_settings` collection with an env var fallback.
 *
 * Reused for two lists via props: the general notification recipients and
 * the lead-alert-only recipients (`endpoint="lead-notification-recipients"`,
 * `allowEmpty` so an empty list means "inherit the general list").
 */
export default function NotificationRecipientsCard({
  endpoint = "notification-recipients",
  title = "Email Recipients",
  description = "Inboxes that receive new-lead, contact, quote, and invoice alerts. Add a personal email as a backup so a Workspace quarantine never silently swallows a lead.",
  allowEmpty = false,
  testId = "notification-recipients-card",
  testLabel = "Send Test",
}) {
  const [recipients, setRecipients] = useState([]);
  const [source, setSource] = useState("env");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/${endpoint}`);
      setRecipients(data.recipients || []);
      setSource(data.source || "env");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load recipients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [endpoint]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addDraft = () => {
    const v = draft.trim();
    if (!v) return;
    if (!EMAIL_RE.test(v)) { toast.error("Not a valid email"); return; }
    if (recipients.map(r => r.toLowerCase()).includes(v.toLowerCase())) {
      toast.error("Already in the list"); return;
    }
    setRecipients(prev => [...prev, v]);
    setDraft("");
  };

  const removeAt = (idx) => setRecipients(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!recipients.length && !allowEmpty) { toast.error("Add at least one email"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/${endpoint}`, { recipients });
      setRecipients(data.recipients || []);
      setSource(data.source || "db");
      toast.success(
        (data.recipients || []).length
          ? "Recipients saved"
          : "Cleared — these alerts will use the general recipient list",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/settings/${endpoint}/test`);
      toast.success(
        data?.subject
          ? `Test sent to ${(data.recipients || []).join(", ")} — subject: ${data.subject}`
          : "Test email sent — check inboxes (and spam)",
        { duration: 10000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  const sourceLabel = { db: "Custom", env: "Default (env)", inherited: "Inherited" }[source] || source;

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-[#0F4C81]" /> {title}
            </CardTitle>
            <CardDescription className="mt-1">
              {description}
            </CardDescription>
          </div>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              source === "db" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
            data-testid="recipients-source-badge"
          >
            {sourceLabel}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="space-y-2" data-testid="recipients-list">
              {recipients.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-2">
                  {allowEmpty
                    ? "Empty — these alerts fall back to the general recipient list above."
                    : "No recipients configured yet."}
                </div>
              ) : (
                recipients.map((email, idx) => (
                  <div
                    key={`${email}-${idx}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg"
                    data-testid={`recipient-row-${idx}`}
                  >
                    <span className="font-mono text-sm break-all">{email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => removeAt(idx)}
                      data-testid={`remove-recipient-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="add another@example.com"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDraft(); } }}
                className="flex-1"
                data-testid="recipient-input"
              />
              <Button
                onClick={addDraft}
                variant="outline"
                className="border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81]/5"
                data-testid="add-recipient-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#0F4C81] hover:bg-[#0D4270] flex-1"
                data-testid="save-recipients-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Recipients
              </Button>
              <Button
                onClick={sendTest}
                disabled={testing || recipients.length === 0}
                variant="outline"
                className="flex-1"
                data-testid="test-recipients-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {testLabel}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
